import React, { useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { App, DatePicker, Form, InputNumber, Modal } from "antd";

import { apiCloseOptionTrade } from "api/apiOptionStrategies";
import { formatMoney, tradeMultiplier } from "./optionTradeUtils";
import { marketToday } from "./optionStrategyUtils";

const CloseOptionTradeModal = (props) => {
  const { trade, onClose, onSaved } = props;
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [isSaving, setIsSaving] = useState(false);
  const premium = Form.useWatch("premium", form);
  const fees = Form.useWatch("fees", form);
  const userPremiumFee = useSelector(
    (state) => state.main.user.preferences?.premiumFee,
  );

  const closeValue = (premium || 0) * trade.amount * tradeMultiplier(trade);
  const netPnl =
    closeValue - trade.openValue - (trade.premiumFee || 0) - (fees || 0);

  const onFinish = async (values) => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      await apiCloseOptionTrade({
        tradeId: trade._id,
        premium: values.premium,
        fees: values.fees,
        closeDate: values.closeDate.format("YYYY-MM-DD"),
        underlyingPrice: values.underlyingPrice,
        iv: values.ivPercent != null ? values.ivPercent / 100 : undefined,
      });
      message.success(`${trade.symbol} option trade closed`);
      onSaved();
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to close trade");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      title={`Close option trade - ${trade.symbol} ${trade.optionTrade.strikePrice} ${trade.optionTrade.optionType}`}
      open
      onCancel={onClose}
      onOk={() => form.submit()}
      confirmLoading={isSaving}
      okText="Close trade"
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          // an advised exit (15:50 run) prefills its suggested price and quote
          premium: trade.advicedSellPrice,
          fees: userPremiumFee ?? 0,
          closeDate: marketToday(),
          underlyingPrice: trade.optionTrade.exitQuote?.underlyingPrice,
          ivPercent:
            trade.optionTrade.exitQuote?.iv != null
              ? +(trade.optionTrade.exitQuote.iv * 100).toFixed(1)
              : undefined,
        }}
      >
        {trade.orderStatus === "pending-close" && trade.closeReason && (
          <p style={{ color: "#999" }}>Exit advised: {trade.closeReason}</p>
        )}
        <Form.Item
          name="premium"
          label="Sell premium per share"
          extra={`Value: $${formatMoney(closeValue)} · net P&L: $${formatMoney(netPnl)} (enter 0 if it expired worthless)`}
          rules={[{ required: true, message: "Premium is required" }]}
        >
          <InputNumber style={{ width: "100%" }} min={0} step={0.01} />
        </Form.Item>
        <Form.Item name="fees" label="Commission">
          <InputNumber style={{ width: "100%" }} min={0} step={0.01} />
        </Form.Item>
        <Form.Item
          name="closeDate"
          label="Close date"
          rules={[{ required: true, message: "Close date is required" }]}
        >
          <DatePicker style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item name="underlyingPrice" label="Underlying price (optional)">
          <InputNumber style={{ width: "100%" }} min={0} step={0.01} />
        </Form.Item>
        <Form.Item
          name="ivPercent"
          label="IV at close (optional)"
          tooltip="Used with the underlying price for the model check: what the opening IV would have priced this exit at"
        >
          <InputNumber
            style={{ width: "100%" }}
            min={0}
            step={0.1}
            addonAfter="%"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

CloseOptionTradeModal.propTypes = {
  trade: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

export default CloseOptionTradeModal;
